import React, { ChangeEvent, FC, FocusEvent } from "react";
import { Control, Controller, useFieldArray } from "react-hook-form";
import { useSelector } from "react-redux";

import { Button, Icon, Input, Label } from "../../common";
import { useInvoiceCurrencies } from "../../../hooks/useInvoiceCurrencies";
import { useValidateInvoiceAmountThreshold } from "../../../hooks/useValidateInvoiceAmountThreshold";
import { selectInvoice } from "../../../store/reducers/invoice";
import { Invoice } from "../../../types/InvoiceTypes";
import { isNumber } from "../../../utils/numbers";

type Props = {
  control: Control<Invoice>;
};

export const ItemsList: FC<Props> = ({ control }) => {
  const invoice = useSelector(selectInvoice);
  const invoiceCurrencies = useInvoiceCurrencies(invoice);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const { msg: amountThresholdMsg } =
    useValidateInvoiceAmountThreshold(invoice);

  if (!invoice) {
    return <></>;
  }

  const inputCurrencySymbol = invoiceCurrencies!.input!.symbol;

  return (
    <div className="flex flex-col gap-6">
      {fields.map((item, index) => (
        <div key={item.id} className="flex flex-col">
          <Label
            label={`Job #${index + 1}`}
            labelClassName="uppercase"
            valueClassName="flex flex-col gap-2"
          >
            <Controller
              name={`items.${index}.description`}
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Describe what was done"
                  maxLength={250}
                  {...field}
                />
              )}
            />

            <div className="flex gap-2">
              <Controller
                name={`items.${index}.rate`}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    type="text"
                    {...field}
                    min="0"
                    step="1"
                    placeholder="Amount"
                    postfix={inputCurrencySymbol}
                    postfixClassName="pr-6"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (!isNumber(e.target.value)) {
                        e.preventDefault();
                        return;
                      }

                      field.onChange(e);
                    }}
                    onBlur={(e: FocusEvent<HTMLInputElement>) => {
                      e.target.value = Number(e.target.value).toFixed(2);
                      field.onChange(e);
                    }}
                    wrapperClassName="grow"
                  />
                )}
              />

              {index !== 0 && (
                <Button
                  variant={Button.variant.SECONDARY}
                  onClick={() => remove(index)}
                  icon
                >
                  <Icon icon="Minus" size={24} disableFill />
                </Button>
              )}
            </div>
          </Label>
        </div>
      ))}

      <Button
        variant={Button.variant.TERTIARY}
        onClick={() => append({ description: "", rate: 0 })}
      >
        <Icon icon="Add" size={24} disableFill /> Add row
      </Button>

      {amountThresholdMsg}
    </div>
  );
};
